<script setup lang="ts">
import { computed, onBeforeUnmount, ref, watch } from 'vue'
import { canRouteAudio, useCall } from '../stores/call'
import Icon from './Icon.vue'
import AvSettings from './AvSettings.vue'
import CallTile from './CallTile.vue'

const call = useCall()

const devicesOpen = ref(false)
const others = computed(() => call.tiles.filter((t) => t.key !== call.focused?.key))
const layout = computed(() => (!call.expanded ? 'strip' : call.focused ? 'focus' : 'grid'))

// Grid layout: pick the column count that makes 16:9 tiles as large as the area allows.
const area = ref<HTMLElement | null>(null)
const size = ref({ w: 0, h: 0 })
const observer = new ResizeObserver(([e]) => {
  if (e) size.value = { w: e.contentRect.width, h: e.contentRect.height }
})
watch(area, (el, old) => {
  if (old) observer.unobserve(old)
  if (el) observer.observe(el)
})
onBeforeUnmount(() => observer.disconnect())

const tileWidth = computed(() => {
  const n = call.tiles.length
  const { w, h } = size.value
  if (!n || !w || !h) return 0
  let best = 0
  for (let cols = 1; cols <= n; cols++) {
    const rows = Math.ceil(n / cols)
    best = Math.max(best, Math.min((w - (cols - 1)) / cols, ((h - (rows - 1)) / rows) * (16 / 9)))
  }
  return Math.floor(best)
})
</script>

<template>
  <section
    class="flex flex-col bg-bg-primary"
    :class="call.expanded ? 'min-h-0 min-w-0 flex-1' : 'shrink-0 border-b border-border px-6 py-4'"
    data-testid="call-panel"
    :data-layout="layout"
  >
    <div class="mono flex items-center gap-4 uppercase" :class="call.expanded ? 'border-b border-border px-4 py-2' : 'mb-3'">
      <span class="flex items-center gap-1.5 text-text-primary"><span class="h-1.5 w-1.5 animate-blink bg-signal" />In call</span>
      <span class="text-text-muted">{{ String(call.members.length).padStart(2, '0') }} ch</span>
      <span v-if="call.e2ee" class="text-text-muted max-sm:hidden" data-testid="call-e2ee">frame e2ee · end-to-end encrypted</span>
      <span v-else class="text-signal">connecting encryption…</span>
    </div>

    <!-- Strip: small tiles above the conversation. -->
    <div v-if="layout === 'strip'" class="flex flex-wrap gap-px">
      <div v-for="t in call.tiles" :key="t.key" class="aspect-video w-[calc(50%-1px)] border border-border sm:w-52">
        <CallTile :tile="t" />
      </div>
    </div>

    <!-- Focus: one tile large, the rest in a filmstrip. -->
    <div v-else-if="layout === 'focus' && call.focused" class="flex min-h-0 flex-1 flex-col bg-black">
      <div class="min-h-0 flex-1">
        <CallTile :key="call.focused.key" :tile="call.focused" large />
      </div>
      <div
        v-if="others.length"
        class="scroll-thin flex h-20 shrink-0 gap-px overflow-x-auto border-t border-border md:h-28"
        data-testid="call-filmstrip"
      >
        <div v-for="t in others" :key="t.key" class="aspect-video h-full shrink-0">
          <CallTile :tile="t" />
        </div>
      </div>
    </div>

    <!-- Grid: everyone, as large as fits. -->
    <div v-else ref="area" class="field-grid flex min-h-0 flex-1 flex-wrap content-center justify-center gap-px overflow-hidden">
      <div v-for="t in call.tiles" :key="t.key" class="aspect-video" :style="{ width: `${tileWidth}px` }">
        <CallTile :tile="t" />
      </div>
    </div>

    <div v-if="devicesOpen" class="border-t border-border px-4 py-3" :class="call.expanded ? '' : 'mt-3 border'">
      <div class="mx-auto max-w-md"><AvSettings compact /></div>
    </div>

    <div class="flex flex-wrap justify-center gap-2" :class="call.expanded ? 'border-t border-border px-4 py-3' : 'mt-3'">
      <button
        class="btn-icon"
        :class="call.mic ? '' : 'text-signal'"
        :aria-pressed="!call.mic"
        :aria-label="call.mic ? 'Mute' : 'Unmute'"
        :title="call.mic ? 'Mute' : 'Unmute'"
        data-testid="call-mic"
        @click="call.toggleMic()"
      >
        <Icon :name="call.mic ? 'mic' : 'mic-off'" :size="5" />
      </button>
      <button
        class="btn-icon"
        :aria-pressed="call.camera"
        :aria-label="call.camera ? 'Stop video' : 'Video'"
        :title="call.camera ? 'Stop video' : 'Video'"
        data-testid="call-camera"
        @click="call.toggleCamera()"
      >
        <Icon :name="call.camera ? 'video' : 'video-off'" :size="5" />
      </button>
      <button
        class="btn-icon"
        :aria-pressed="call.screen"
        :aria-label="call.screen ? 'Stop sharing' : 'Share screen'"
        :title="call.screen ? 'Stop sharing' : 'Share screen'"
        data-testid="call-screen"
        @click="call.toggleScreen()"
      >
        <Icon name="screen" :size="5" />
      </button>
      <button
        v-if="canRouteAudio"
        class="btn-icon"
        :aria-label="call.speaker ? 'Speaker (switch to the earpiece)' : 'Earpiece (switch to the speaker)'"
        :title="call.speaker ? 'Speaker' : 'Earpiece'"
        data-testid="call-speaker"
        @click="call.toggleSpeaker()"
      >
        <Icon :name="call.speaker ? 'speaker' : 'phone'" :size="5" />
      </button>
      <button
        class="btn-icon"
        :aria-pressed="devicesOpen"
        aria-label="Devices"
        title="Devices"
        data-testid="call-devices"
        @click="devicesOpen = !devicesOpen"
      >
        <Icon name="gear" :size="5" />
      </button>
      <button
        class="btn-icon"
        :aria-pressed="call.expanded"
        :aria-label="call.expanded ? 'Collapse' : 'Expand'"
        :title="call.expanded ? 'Collapse' : 'Expand'"
        data-testid="call-expand"
        @click="call.expanded = !call.expanded"
      >
        <Icon :name="call.expanded ? 'collapse' : 'expand'" :size="5" />
      </button>
      <button
        v-if="call.expanded"
        class="btn-icon"
        :aria-pressed="call.chatOpen"
        :aria-label="call.chatOpen ? 'Hide chat' : 'Chat'"
        :title="call.chatOpen ? 'Hide chat' : 'Chat'"
        data-testid="call-chat"
        @click="call.chatOpen = !call.chatOpen"
      >
        <Icon name="chat" :size="5" />
      </button>
      <button
        class="btn-danger min-w-8 px-1.5 max-md:h-11 max-md:min-w-11"
        aria-label="Leave"
        title="Leave"
        data-testid="call-leave"
        @click="call.leave()"
      >
        <Icon name="hangup" :size="5" />
      </button>
    </div>
  </section>
</template>
